import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { isValidElement, Component } from 'react';
import styled, { css } from 'styled-components';
import AccordionTransition, { AccordionTransitionWrapper } from '../transitions/AccordionTransition';
import classNames from 'classnames';
import Controllable from '../decorators/Controllable';
import HoverProvider from '../providers/HoverProvider';
import SyntheticEvent from '../core/SyntheticEvent';
import UIIcon from '../icon/UIIcon';
import UILink from '../link/UILink';
import UINavItemLabel from '../nav/UINavItemLabel';
import { hidden } from '../utils/propTypes/decorators';
import { isRenderable } from '../utils/propTypes/componentProp';
import { NAV_LIST_TRANSITION_ANIMATION_TIMING } from 'HubStyleTokens/times';
import { BASE_FONT_SIZE, MERCURY_LAYER, NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_RIGHT, BASE_ICON_SPACING_X, NAV_ITEM_PADDING_Y, BASE_LINE_HEIGHT } from 'HubStyleTokens/sizes';
import { setFontSmoothing, setBorderRadius, setFontSize, FONT_FAMILIES } from '../utils/Styles';
import { CALYPSO } from 'HubStyleTokens/colors';
import { BASE_LINK_COLOR, SIDENAV_FOREGROUND_COLOR, SIDENAV_HOVERED_BACKGROUND_COLOR, SIDENAV_SELECTED_BACKGROUND_COLOR, SIDENAV_SELECTED_BORDER_COLOR, BASE_FONT_COLOR } from 'HubStyleTokens/theme';
import { emCalc } from '../core/Style';
import { uniqueId } from '../utils/underscore';
const ICON_SIZE = 'xxs';
const NavListItemTransitionWrapper = styled(AccordionTransitionWrapper).withConfig({
  displayName: "UINavListItem__NavListItemTransitionWrapper",
  componentId: "sk496d-0"
})(["margin-left:-", ";padding-left:", ";overflow:", ";"], NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_LEFT, ({
  transitioning
}) => transitioning ? 'hidden' : 'visible');
const linkStyles = css(["display:flex;align-items:center;", ";", ";", ";color:", ";left:-", ";line-height:", ";padding:", " ", " ", " ", ";position:relative;text-decoration:none;width:calc(100% + ", ");cursor:", ";"], setFontSmoothing('auto'), FONT_FAMILIES.default, setFontSize(BASE_FONT_SIZE), SIDENAV_FOREGROUND_COLOR, NAV_ITEM_PADDING_LEFT, BASE_LINE_HEIGHT, NAV_ITEM_PADDING_Y, NAV_ITEM_PADDING_RIGHT, NAV_ITEM_PADDING_Y, NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_LEFT, ({
  disabled
}) => disabled === true ? 'not-allowed' : 'pointer');
const activeStyles = css(["color:", ";background-color:", ";", ";&::before{content:'';border-left:4px solid ", ";height:100%;left:0;position:absolute;top:0;", "}"], BASE_FONT_COLOR, SIDENAV_SELECTED_BACKGROUND_COLOR, setBorderRadius(), SIDENAV_SELECTED_BORDER_COLOR, setBorderRadius());
const hoveredStyles = css(["", ";color:", ";text-decoration:none;:not(.private-folder-nav-item){background-color:", ";}"], setBorderRadius(), BASE_FONT_COLOR, SIDENAV_HOVERED_BACKGROUND_COLOR);
const parentStyles = css(["user-select:none;"]);
const StyledLink = styled(props => {
  const {
    linkComponent: Link
  } = props,
        rest = _objectWithoutPropertiesLoose(props, ["linkComponent", "active", "hasChildren", "hovered", "responsive"]);

  return /*#__PURE__*/_jsx(Link, Object.assign({}, rest));
}).withConfig({
  displayName: "UINavListItem__StyledLink",
  componentId: "sk496d-1"
})(["", " ", " &&{", ";}", " &.private-nav-item--active{", ";}&:focus{z-index:", ";}.private-tag--unenclosed{line-height:16px;}&& > .private-help-icon{display:inline-flex;}&& > .private-icon:not(.private-icon-circle__inner):not(:first-child),&& > .private-help-icon:not(:first-child) .private-icon{margin-left:", ";margin-right:0;}"], linkStyles, ({
  active
}) => active ? activeStyles : undefined, ({
  hovered,
  disabled
}) => hovered && !disabled ? hoveredStyles : undefined, ({
  hasChildren
}) => hasChildren ? parentStyles : undefined, activeStyles, MERCURY_LAYER, emCalc(BASE_ICON_SPACING_X));
const Icon = styled(UIIcon).withConfig({
  displayName: "UINavListItem__Icon",
  componentId: "sk496d-2"
})(["margin-right:", ";"], ({
  placement
}) => placement === 'right' && '0 !important');
const NavItemLabel = styled(UINavItemLabel).withConfig({
  displayName: "UINavListItem__NavItemLabel",
  componentId: "sk496d-3"
})(["font-size:", ";flex-grow:1;"], BASE_FONT_SIZE);

class UINavListItem extends Component {
  constructor(props) {
    super(props);

    this.handleClick = evt => {
      const {
        onClick,
        onOpenChange,
        open
      } = this.props;
      if (onClick) onClick(evt);

      if (!evt.defaultPrevented && onOpenChange) {
        onOpenChange(SyntheticEvent(!open));
      }
    };

    this._expandableContentId = uniqueId('UINavListItem-expandable-content-');
  }

  render() {
    const _this$props = this.props,
          {
      active,
      children,
      className,
      count,
      duration,
      iconLeft,
      iconRight,
      itemClassName,
      linkComponent,
      open,
      _rootRenderer,
      title
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["active", "children", "className", "count", "duration", "hovered", "iconLeft", "iconRight", "itemClassName", "linkComponent", "onBlur", "onClick", "onOpenChange", "open", "_rootRenderer", "title", "value"]);

    const renderIcon = (icon, placement) => {
      if (!icon) return null;

      if ( /*#__PURE__*/isValidElement(icon)) {
        return icon;
      }

      if (typeof icon === 'function') {
        return icon({
          children,
          open,
          size: ICON_SIZE,
          placement
        });
      }

      if (typeof icon === 'string') {
        return /*#__PURE__*/_jsx(Icon, {
          name: icon,
          size: ICON_SIZE,
          placement: placement,
          color: placement === 'right' ? BASE_LINK_COLOR : CALYPSO
        });
      }

      return null;
    };

    const hasChildren = isRenderable(children);
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => {
        const {
          hovered
        } = hoverProviderProps,
              hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, ["hovered"]);

        const classes = classNames(className, 'private-nav-item', active && 'private-nav-item--active');
        return /*#__PURE__*/_jsxs("li", {
          className: itemClassName,
          children: [/*#__PURE__*/_jsxs(StyledLink, Object.assign({
            active: active,
            hasChildren: hasChildren,
            hovered: hovered || undefined,
            linkComponent: linkComponent,
            "aria-expanded": hasChildren ? open : null,
            "aria-controls": hasChildren ? this._expandableContentId : null
          }, rest, {}, hoverProviderRestProps, {
            "aria-current": active ? 'true' : null,
            className: classes,
            onClick: this.handleClick,
            children: [renderIcon(iconLeft, 'left'), /*#__PURE__*/_jsx(NavItemLabel, {
              count: count,
              children: title
            }), renderIcon(iconRight, 'right')]
          })), /*#__PURE__*/_jsx(AccordionTransition, {
            id: this._expandableContentId,
            open: open,
            duration: duration,
            Wrapper: NavListItemTransitionWrapper,
            children: hasChildren && _rootRenderer && _rootRenderer(children)
          })]
        });
      }
    }));
  }

}

const propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node,
  count: PropTypes.node,
  external: PropTypes.bool,
  disabled: PropTypes.bool,
  duration: PropTypes.number,
  hovered: hidden(PropTypes.bool),
  iconLeft: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  iconRight: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  itemClassName: PropTypes.string,
  linkComponent: PropTypes.elementType,
  onClick: PropTypes.func,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
  value: PropTypes.node,
  _rootRenderer: PropTypes.func
};
UINavListItem.propTypes = propTypes;
UINavListItem.defaultProps = {
  active: false,
  external: false,
  disabled: false,
  duration: parseInt(NAV_LIST_TRANSITION_ANIMATION_TIMING, 10),
  linkComponent: UILink,
  open: false
};
UINavListItem.displayName = 'UINavListItem';
export default Controllable(UINavListItem, ['open']);